// PlacesFields — the PLACES grammar as one piece: "Zones and clusters" (the
// family's tree picker — a zone's box ticks everything inside, its clusters read
// Included) then "Extra sites" (the site lookup under the host's rule, where a
// site a picked zone or cluster already covers reads "Included · via …" and
// cannot be picked; a zone or cluster picked LATER drops the sites it covers
// from the draft). The highest pick (ADR-050 §3): the server absorbs a covered
// pick again on every save, the piece says so first. Controlled and
// presentational over ONE `PlacesValue`; the hosts — the scope editor's Sites
// section, a team's "Works in" — wrap it in their section and add their own
// plain pickers (depots and pools have no grammar). The words are the piece's
// (`common:places.*`); the rule is the two pure functions below, unit-tested.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SiteLookupRow, SiteLookupRule } from '@ems-app/schemas/sites/lookup';
import type { ContainerListRule } from '@ems-app/schemas/zones/requests';
import { clusterValue, scopeTreeSource, splitTreeValues, zoneValue } from '../../hooks/useScopeTree.js';
import { siteSource, useNamedSites } from '../../hooks/useSites.js';
import { useSiteFace } from './lookupFaces.js';
import { Picker } from './Picker.js';

export interface PlacesValue {
  zone_ids: number[];
  cluster_ids: number[];
  site_ids: string[];
}

export interface PlacesFieldsProps {
  projectId: string;
  value: PlacesValue;
  onChange: (next: PlacesValue) => void;
  /** The tree's rule — `manage` (the default): the viewer's own-scope zones and clusters. */
  treeRule?: ContainerListRule;
  /** The site lookup's rule — `in_coverage` (the default): the sites the viewer's reach covers. */
  siteRule?: SiteLookupRule;
  disabled?: boolean;
}

type PlaceOfSite = Pick<SiteLookupRow, 'zone_id' | 'cluster_id'>;

/** The container a picked zone or cluster covers this site through — its name — or null when the site stands alone. */
export function coveredVia(row: PlaceOfSite & Pick<SiteLookupRow, 'zone_name' | 'cluster_name'>, zoneIds: ReadonlySet<number>, clusterIds: ReadonlySet<number>): string | null {
  if (zoneIds.has(row.zone_id)) return row.zone_name;
  if (clusterIds.has(row.cluster_id)) return row.cluster_name;
  return null;
}

/** The chosen sites a zone or cluster picked later does NOT cover — a site not named yet is kept (the server absorbs it). */
export function dropCoveredSites(siteIds: readonly string[], byId: ReadonlyMap<string, PlaceOfSite>, zoneIds: ReadonlySet<number>, clusterIds: ReadonlySet<number>): string[] {
  return siteIds.filter((id) => {
    const s = byId.get(id);
    return s === undefined || (!zoneIds.has(s.zone_id) && !clusterIds.has(s.cluster_id));
  });
}

export function PlacesFields({ projectId, value, onChange, treeRule = 'manage', siteRule = 'in_coverage', disabled = false }: PlacesFieldsProps) {
  const { t } = useTranslation('common');
  const siteFace = useSiteFace();
  // The tree's values are kinded (`zone:<id>` · `cluster:<id>`): one field holds both kinds.
  const treeValue = useMemo(() => [...value.zone_ids.map(zoneValue), ...value.cluster_ids.map(clusterValue)], [value.zone_ids, value.cluster_ids]);
  const tree = useMemo(() => scopeTreeSource(projectId, treeRule), [projectId, treeRule]);
  const zonePicked = useMemo(() => new Set(value.zone_ids), [value.zone_ids]);
  const clusterPicked = useMemo(() => new Set(value.cluster_ids), [value.cluster_ids]);
  // The chosen sites known by their rows (the lookup's cache — the same source the picker names them from).
  const chosen = useNamedSites(projectId, value.site_ids, { rule: siteRule });
  const sites = useMemo(
    () =>
      siteSource(projectId, siteRule, (row: SiteLookupRow) => {
        const via = coveredVia(row, zonePicked, clusterPicked);
        const base = siteFace(row);
        return via ? { ...base, description: t('places.includedVia', { name: via }), disabled: true } : base;
      }),
    [projectId, siteRule, zonePicked, clusterPicked, siteFace, t],
  );
  return (
    <>
      <Picker
        multiple
        selectedDisplay="tags"
        source={tree}
        label={t('places.zonesAndClusters')}
        placeholder={t('places.zonesPlaceholder')}
        value={treeValue}
        onChange={(next) => {
          const { zoneIds, clusterIds } = splitTreeValues(next);
          onChange({ zone_ids: zoneIds, cluster_ids: clusterIds, site_ids: dropCoveredSites(value.site_ids, chosen.byId, new Set(zoneIds), new Set(clusterIds)) });
        }}
        disabled={disabled}
      />
      <Picker multiple selectedDisplay="tags" source={sites} label={t('places.extraSites')} placeholder={t('places.sitesPlaceholder')} value={value.site_ids} onChange={(site_ids) => onChange({ ...value, site_ids })} disabled={disabled} />
    </>
  );
}
